import { useCallback } from 'react';

import type { RoutineRunnerHandle } from '@sbrc/services';

import { useRoutineRunnerHandle } from './useRoutineRunnerHandle';

// Same window as the REST endpoint's 10 x 300 ms poll.
export const ROS_CONTROL_CONFIRM_TIMEOUT_MS = 3000;

const ROS_CONTROL_POLL_INTERVAL_MS = 100;

export const ROS_CONTROL_NOT_ENABLED_MESSAGE =
  'The robot did not start ROS2 arm control. Make sure the arm is ready to ' +
  'move, and that services were restarted after the ROS2 API setting last ' +
  'changed.';

export const ROS_CONTROL_NOT_DISABLED_MESSAGE =
  'The robot did not stop ROS2 arm control.';

function waitForReportedRosControl(
  handle: RoutineRunnerHandle,
  enabled: boolean,
): Promise<void> {
  const deadline = Date.now() + ROS_CONTROL_CONFIRM_TIMEOUT_MS;

  return new Promise((resolve, reject) => {
    const check = () => {
      if (handle.getState()?.kinematicState.rosControl === enabled) {
        resolve();

        return;
      }

      if (Date.now() >= deadline) {
        reject(
          new Error(
            enabled
              ? ROS_CONTROL_NOT_ENABLED_MESSAGE
              : ROS_CONTROL_NOT_DISABLED_MESSAGE,
          ),
        );

        return;
      }

      setTimeout(check, ROS_CONTROL_POLL_INTERVAL_MS);
    };

    check();
  });
}

/**
 * Sets ROS control on the live robot and resolves only once the robot reports
 * the requested state. The request itself never fails: botman and
 * arm-control-bot both resolve after silently refusing, so the reported state
 * is the only proof.
 */
export const useROSControl = () => {
  const routineRunnerHandle = useRoutineRunnerHandle({ isVizbot: false });

  return useCallback(
    async (enabled: boolean): Promise<void> => {
      await routineRunnerHandle.setROSControlEnabled(enabled);
      await waitForReportedRosControl(routineRunnerHandle, enabled);
    },
    [routineRunnerHandle],
  );
};
